/**
 * Deeply merges two objects.
 * - Does NOT mutate original objects
 * - Arrays are replaced (not merged)
 * - Only merges plain objects
 * - Always returns a new reference
 */
export function deepMerge<T extends object, U extends object>(target: T, source: U): T & U {
  if (!isObject(target) || !isObject(source)) {
    // Defensive: callers typed as objects can still pass a runtime non-object,
    // in which case source wins wholesale and the T & U claim cannot be proven.
    return source as T & U;
  }

  const result: Record<string, unknown> = { ...target };
  const sourceRecord = source as Record<string, unknown>;
  const targetRecord = target as Record<string, unknown>;

  for (const key of Object.keys(source)) {
    const sourceValue = sourceRecord[key];
    const targetValue = targetRecord[key];

    if (Array.isArray(sourceValue)) {
      // Replace arrays
      result[key] = [...sourceValue];
    } else if (isObject(sourceValue) && isObject(targetValue)) {
      // Recursively merge nested objects
      result[key] = deepMerge(targetValue, sourceValue);
    } else {
      // Replace primitives, nulls, dates, functions, etc.
      result[key] = sourceValue;
    }
  }

  // Structurally the merge of every key of T and U; TypeScript cannot track
  // that through a keyed loop, so this is the one cast the algorithm needs.
  return result as T & U;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
